import React from 'react';
import { FileText, Download, ExternalLink, Search } from 'lucide-react';

const Documents = () => {
  const documentCategories = [
    {
      id: 'guidelines',
      title: 'Submission Guidelines',
      documents: [
        {
          id: 'guide-01',
          title: 'Building Design Submission Guidelines',
          description: 'Comprehensive guide on how to prepare and submit your building designs.',
          type: 'PDF',
          size: '2.4 MB',
          date: 'January 15, 2025',
        },
        {
          id: 'guide-02',
          title: 'IFC Model Requirements',
          description: 'Technical specifications for 3D IFC models submission.',
          type: 'PDF',
          size: '1.8 MB',
          date: 'February 3, 2025',
        },
        {
          id: 'guide-03',
          title: 'PDF Drawing Standards',
          description: 'Requirements for PDF drawings including scales, annotations, and layers.',
          type: 'PDF',
          size: '3.2 MB',
          date: 'January 20, 2025',
        },
      ]
    },
    {
      id: 'forms',
      title: 'Required Forms',
      documents: [
        {
          id: 'form-01',
          title: 'Building Approval Application Form',
          description: 'Official application form for building design approval.',
          type: 'PDF',
          size: '520 KB',
          date: 'December 10, 2024',
        },
        {
          id: 'form-02',
          title: 'Owner Declaration Form',
          description: 'Legal declaration to be signed by the property owner.',
          type: 'PDF',
          size: '390 KB',
          date: 'November 29, 2024',
        },
        {
          id: 'form-03',
          title: 'Architect/Engineer Certification',
          description: 'Professional certification form for project designers.',
          type: 'PDF',
          size: '410 KB',
          date: 'January 8, 2025',
        },
      ]
    },
    {
      id: 'codes',
      title: 'Building Codes & Regulations',
      documents: [
        {
          id: 'code-01',
          title: 'National Building Code 2025',
          description: 'Latest version of the national building code with annotations.',
          type: 'PDF',
          size: '12.8 MB',
          date: 'January 1, 2025',
        },
        {
          id: 'code-02',
          title: 'Fire Safety Requirements',
          description: 'Current fire safety code requirements for buildings.',
          type: 'PDF',
          size: '4.2 MB',
          date: 'October 15, 2024',
        },
        {
          id: 'code-03',
          title: 'Accessibility Standards',
          description: 'Requirements for building accessibility and universal design.',
          type: 'PDF',
          size: '3.7 MB',
          date: 'December 5, 2024',
        },
      ]
    },
  ];

  return (
    <div className="min-h-screen bg-white pt-16">
      {/* Header Banner */}
      <section className="bg-blue-700 py-24 bglog">
        <div className="container mx-auto px-6">
          <h1 className="text-4xl font-bold text-white text-center">Useful Documents</h1>
        </div>
      </section>

      {/* Search Section */}
      <section className="py-8 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto relative">
            <input
              type="text"
              placeholder="Search for documents..."
              className="w-full p-4 pl-12 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent"
            />
            <Search className="absolute left-4 top-4 text-gray-400" size={20} />
          </div>
        </div>
      </section>

      {/* Documents Listing */}
      <section className="py-16 container mx-auto px-6">
        {documentCategories.map((category) => (
          <div key={category.id} className="mb-16">
            <h2 className="text-2xl font-bold mb-8 pb-2 border-b-2 border-gray-200">{category.title}</h2>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {category.documents.map((document) => (
                <div key={document.id} className="border rounded-lg overflow-hidden hover:shadow-md transition duration-300">
                  <div className="bg-gray-50 p-4 flex items-center">
                    <FileText className="text-blue-700 mr-3" size={24} />
                    <h3 className="text-lg font-semibold truncate">{document.title}</h3>
                  </div>
                  <div className="p-4">
                    <p className="text-gray-600 text-sm mb-4">{document.description}</p>
                    <div className="flex justify-between text-sm text-gray-500 mb-4">
                      <span className="flex items-center">
                        <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs mr-2">{document.type}</span>
                        {document.size}
                      </span>
                      <span>{document.date}</span>
                    </div>
                    <div className="flex space-x-2">
                      <button className="flex items-center text-sm font-medium text-blue-700 hover:text-blue-800 transition duration-300">
                        <Download size={16} className="mr-1" />
                        Download
                      </button>
                      <button className="flex items-center text-sm font-medium text-blue-700 hover:text-blue-800 transition duration-300">
                        <ExternalLink size={16} className="mr-1" />
                        Preview
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </section>

      {/* External Resources */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-6">
          <h2 className="text-2xl font-bold mb-8 text-center">External Resources</h2>
          
          <div className="max-w-4xl mx-auto space-y-4">
            <div className="bg-white p-4 rounded-lg border border-gray-200 flex items-center justify-between">
              <div className="flex items-center">
                <ExternalLink className="text-blue-700 mr-3" size={20} />
                <span className="font-medium">National Building Authority Official Website</span>
              </div>
              <a href="#" className="text-blue-700 hover:text-blue-800 transition duration-300">Visit Site</a>
            </div>
            
            <div className="bg-white p-4 rounded-lg border border-gray-200 flex items-center justify-between">
              <div className="flex items-center">
                <ExternalLink className="text-blue-700 mr-3" size={20} />
                <span className="font-medium">Building Standards Database</span>
              </div>
              <a href="#" className="text-blue-700 hover:text-blue-800 transition duration-300">Visit Site</a>
            </div>
            
            <div className="bg-white p-4 rounded-lg border border-gray-200 flex items-center justify-between">
              <div className="flex items-center">
                <ExternalLink className="text-blue-700 mr-3" size={20} />
                <span className="font-medium">International Building Code (IBC) References</span>
              </div>
              <a href="#" className="text-blue-700 hover:text-blue-800 transition duration-300">Visit Site</a>
            </div>
            
            <div className="bg-white p-4 rounded-lg border border-gray-200 flex items-center justify-between">
              <div className="flex items-center">
                <ExternalLink className="text-blue-700 mr-3" size={20} />
                <span className="font-medium">IFC Model Validation Tools</span>
              </div>
              <a href="#" className="text-blue-700 hover:text-blue-800 transition duration-300">Visit Site</a>
            </div>
          </div>
        </div>
      </section>

      {/* Request Document Section */}
      <section className="py-16 container mx-auto px-6">
        <div className="max-w-3xl mx-auto bg-blue-50 p-8 rounded-lg border border-blue-100">
          <h2 className="text-2xl font-bold mb-4 text-center">Can't Find What You Need?</h2>
          <p className="text-gray-600 text-center mb-6">
            If you can't find a specific document or resource, please fill out the form below and our team will assist you.
          </p>
          
          <form className="space-y-4">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
              <input
                type="text"
                id="name"
                className="w-full p-3 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                placeholder="Enter your full name"
              />
            </div>
            
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
              <input
                type="email"
                id="email"
                className="w-full p-3 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                placeholder="Enter your email address"
              />
            </div>
            
            <div>
              <label htmlFor="document" className="block text-sm font-medium text-gray-700 mb-1">Document Needed</label>
              <input
                type="text"
                id="document"
                className="w-full p-3 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                placeholder="Describe the document you're looking for"
              />
            </div>
            
            <div>
              <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">Additional Information</label>
              <textarea
                id="message"
                rows="4"
                className="w-full p-3 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                placeholder="Provide any additional details that might help us locate the document"
              ></textarea>
            </div>
            
            <div className="text-center">
              <button
                type="submit"
                className="bg-blue-700 text-white py-3 px-8 rounded-md font-medium hover:bg-blue-800 transition duration-300"
              >
                Submit Request
              </button>
            </div>
          </form>
        </div>
      </section>
    </div>
  );
};

export default Documents;